import {
  AnswerPromptRequest,
  CreatePromptRequest,
  DeletePromptRequest,
  GetPromptRequest,
  ListPromptsForJobRequest,
  Prompt as PromptProto,
  PromptDesignee as DesigneeProto,
  PromptQuestionInput,
} from '@taxfyle/api-internal/internal/prompt_pb'
import {
  BoolInputValue,
  DocumentsInputValue,
  InputValue,
  TextInputValue,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { PrimeMember } from 'iam/MemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { UserIdLookup } from 'iam/UserIdLookup'
import { collection } from 'infra/collection'
import { PrimeJob } from 'jobs/JobStore'
import { PromptDto } from 'jobs/prompts/dtos'
import { PromptEvent } from 'jobs/prompts/prompt-events'
import { PromptInput, PromptType } from 'jobs/prompts/prompt-types'
import {
  QuestionInputValue,
  DocumentsQuestionInputValue,
} from 'jobs/questions/input-value-types'
import { concatMap } from 'rxjs/operators'
import { CursorPaged, Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import { filterNonNil } from 'utils/rx-util'
import { PromptsAPI } from './API'
import {
  AnswerPromptPayload,
  CreatePrompt,
  DeletePromptPayload,
  ListPromptsPayload,
} from './payloads'
import { Prompt, PromptDesignee } from './Prompt'
import { DocumentUploadPrompt } from './prompt-types'
import TypeCase = RealtimeEvent.TypeCase

/**
 * Prompt prime delegate.
 */
export type PrimePrompt = (primeProps: { id: string }) => Prompt

/**
 * Store for Prompts.
 */
export default class PromptStore {
  constructor(
    private api: PromptsAPI,
    private getCurrentMember: GetCurrentMember,
    private primeMember: PrimeMember,
    private primeJob: PrimeJob,
    private userIdLookup: UserIdLookup
  ) {
    this.events$.subscribe()
  }

  /**
   * Prompt events from the realtime channel.
   */
  readonly events$ = this.api.events$.pipe(
    concatMap((e) => this.mapPromptRealtimeEvent(e)),
    filterNonNil()
  )

  /**
   * Prompts collection.
   */
  prompts = collection<PromptProto.AsObject, Prompt>({
    create: () =>
      new Prompt(this.getCurrentMember, this.primeMember, this.primeJob),
  })

  /**
   * Prime prompt.
   *
   * @param props
   */
  readonly prime: PrimePrompt = ({ id }) => {
    const existing = this.prompts.get(id)
    if (existing) {
      return existing
    }
    return this.prompts.set({ id })
  }

  /**
   * Sends the given prompt.
   */
  async sendPrompt(promptPayload: CreatePrompt): Promise<void> {
    const createPromptResponse = await this.api.createPrompt(
      new CreatePromptRequest()
        .setJobId(promptPayload.jobId)
        .setTitle(promptPayload.title)
        .setDescription(promptPayload.description)
        .setQuestion(mapPromptInput(promptPayload.promptInput))
        .setDesignee(
          match(promptPayload.designee, {
            [PromptDesignee.Client]: () => DesigneeProto.CLIENT,
            [PromptDesignee.Provider]: () => DesigneeProto.PROVIDER,
            [PromptDesignee.Unknown]: () => DesigneeProto.DESIGNEE_UNKNOWN,
          })
        )
    )

    // Intentionally fire-and-forget.
    // We don't want the caller to be aware of failures here, or they may think
    // their Prompt wasn't successfully created.
    this._retrieveUpdated(createPromptResponse.getId()).then()
  }

  /**
   * Looks up prompts by job ID.
   */
  promptsForJob = (jobId: string) =>
    this.prompts.filter((p) => p.job.id === jobId && !p.deleted)

  /**
   * Retrieves the Prompt by the given ID.
   */
  async getPrompt(promptId: Id): Promise<Prompt> {
    promptId = Id.assert(promptId)
    return this.api
      .getPrompt(new GetPromptRequest().setId(promptId))
      .then((response) => response.toObject())
      .then(async (p) => this.enrichPromptWithPrivateIds(p))
      .then(this.prompts.set)
  }

  /**
   * Gets all prompts related to the given job ID.
   */
  async fetchPromptsForJob(
    params: ListPromptsPayload
  ): Promise<CursorPaged<Prompt>> {
    const req = new ListPromptsForJobRequest().setId(params.jobId)
    if (params.after) {
      req.setPageToken(params.after)
    }
    if (params.limit) {
      req.setPageSize(params.limit)
    }

    const result = await this.api.listPromptsForJob(req)
    const promptsList = result.getPromptsList().map((p) => p.toObject())

    return {
      data: this.prompts.set(
        await this.enrichPromptsWithPrivateIds(promptsList)
      ),
      cursor: result.getNextPageToken(),
    }
  }

  /**
   * Answers a prompt
   */
  async answerPrompt(answerPayload: AnswerPromptPayload) {
    const promptId = Id.assert(answerPayload.promptId)

    const answerResponse = await this.api.answerPrompt(
      new AnswerPromptRequest()
        .setId(promptId)
        .setAnswer(mapPromptInputValue(answerPayload.answer))
    )

    // Intentionally fire-and-forget.
    // We don't want the caller to be aware of failures here, or they may think
    // their Prompt wasn't successfully updated.
    this._retrieveUpdated(answerResponse.getId()).then()
  }

  /**
   * Deletes a prompt
   */
  async deletePrompt(deletePayload: DeletePromptPayload) {
    const promptId = Id.assert(deletePayload.promptId)

    const deleteResponse = await this.api.deletePrompt(
      new DeletePromptRequest().setId(promptId)
    )

    // Intentionally fire-and-forget.
    // We don't want the caller to be aware of failures here, or they may think
    // their Prompt wasn't successfully updated.
    this._retrieveUpdated(deleteResponse.getId()).then()
  }

  /**
   * Retrieves the prompt and silently consumes exceptions.
   */
  private async _retrieveUpdated(promptId: string) {
    try {
      return await this.getPrompt(promptId)
    } catch (e) {
      console.warn('Failed to get Prompt after creating/updating', e)
    }
  }

  /**
   * Retrieves all private ids for a list of prompts.
   *
   * @param prompts
   * @returns
   */
  private async enrichPromptsWithPrivateIds(
    prompts: readonly PromptProto.AsObject[]
  ) {
    return Promise.all(
      prompts.map((prompt) => this.enrichPromptWithPrivateIds(prompt))
    )
  }

  /**
   * Gets all private ids for a prompt.
   *
   * @param prompt
   * @returns
   */
  private async enrichPromptWithPrivateIds(
    prompt: PromptProto.AsObject
  ): Promise<PromptDto> {
    const ids = [
      prompt.createdByPublicId,
      prompt.answeredByPublicId,
      prompt.deletedByPublicId,
    ]

    const userIds = await this.userIdLookup.lookupPrivateIds(ids)
    return {
      ...prompt,
      createdById: Id.assert(userIds[prompt.createdByPublicId] ?? undefined),
      answeredById: prompt.answeredByPublicId
        ? Id.assert(userIds[prompt.answeredByPublicId] ?? undefined)
        : null,
      deletedById: prompt.deletedByPublicId
        ? Id.assert(userIds[prompt.deletedByPublicId] ?? undefined)
        : null,
    }
  }

  /**
   * Maps an event from the realtime channel and updates the local representation
   * of the associated prompt.
   *
   * @param event
   * @private
   */
  private async mapPromptRealtimeEvent(
    event: RealtimeEvent
  ): Promise<PromptEvent | null> {
    switch (event.getTypeCase()) {
      case TypeCase.PROMPT_CREATED: {
        const proto = event.getPromptCreated()?.getPrompt()?.toObject()
        if (!proto) {
          return null
        }

        const dto = await this.enrichPromptWithPrivateIds(proto)
        return {
          type: 'PromptCreated',
          prompt: this.prompts.set(dto),
        }
      }

      case TypeCase.PROMPT_ANSWERED: {
        const proto = event.getPromptAnswered()?.getPrompt()?.toObject()
        if (!proto) {
          return null
        }

        const dto = await this.enrichPromptWithPrivateIds(proto)
        return {
          type: 'PromptAnswered',
          prompt: this.prompts.set(dto),
        }
      }

      case TypeCase.PROMPT_DELETED: {
        const proto = event.getPromptDeleted()?.getPrompt()?.toObject()
        if (!proto) {
          return null
        }

        const dto = await this.enrichPromptWithPrivateIds(proto)
        return {
          type: 'PromptDeleted',
          prompt: this.prompts.set(dto),
        }
      }
    }

    return null
  }
}

/**
 * Maps an application prompt input type to a Prompt Question Bool Input proto type.
 */
function toBoolPrompt(_promptInput: PromptInput) {
  return new PromptQuestionInput().setBoolInput(
    new PromptQuestionInput.BoolInput()
  )
}

/**
 * Maps an application prompt input type to a Prompt Question Document Upload
 * Input proto type.
 */
function toDocumentsPrompt(promptInput: DocumentUploadPrompt) {
  return new PromptQuestionInput().setDocumentsInput(
    new PromptQuestionInput.DocumentsInput().setOptionsList(
      promptInput.options.map((o) =>
        new PromptQuestionInput.DocumentsInput.PromptDocumentsOption()
          .setId(o.id)
          .setText(o.title)
      )
    )
  )
}

function toTextPrompt(_promptInput: PromptInput) {
  return new PromptQuestionInput().setTextInput(
    new PromptQuestionInput.TextInput()
  )
}

/**
 * Maps an application prompt input type to its associated Prompt Question Input
 * proto type.
 */
function mapPromptInput(promptInput: PromptInput) {
  switch (promptInput.type) {
    case PromptType.Bool:
      return toBoolPrompt(promptInput)
    case PromptType.DocumentUpload:
      return toDocumentsPrompt(promptInput)
    case PromptType.Text:
      return toTextPrompt(promptInput)
  }
}

function toTextInputValue(promptInputValue: QuestionInputValue) {
  return new InputValue().setTextValue(
    new TextInputValue().setValue(String(promptInputValue.value))
  )
}

function toBoolInputValue(promptInputValue: QuestionInputValue) {
  return new InputValue().setBoolValue(
    new BoolInputValue().setValue(Boolean(promptInputValue.value))
  )
}

function toDocumentsInputValue(promptInputValue: DocumentsQuestionInputValue) {
  const selectedDocumentsList = promptInputValue.value
    .filter((doc) => doc.documentId)
    .map((doc) =>
      new DocumentsInputValue.DocumentsAnswerEntry()
        .setId(doc.id)
        .setLinkedDocumentId(String(doc.documentId))
    )

  return new InputValue().setDocumentsValue(
    new DocumentsInputValue().setSelectedDocumentsList(selectedDocumentsList)
  )
}

/**
 * Maps an application input value type to its associated Input Value
 * proto type.
 */
function mapPromptInputValue(promptInputValue: QuestionInputValue) {
  switch (promptInputValue.type) {
    case 'TEXT':
      return toTextInputValue(promptInputValue)
    case 'BOOL':
      return toBoolInputValue(promptInputValue)
    case 'DOCUMENTS':
      return toDocumentsInputValue(promptInputValue)
  }
}
